import { StrictMode, lazy, Suspense, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'sonner';
import * as Sentry from '@sentry/react';

import './styles/globals.css';
import { ThemeProvider } from './providers/theme-provider';
import { AuthProvider } from './providers/auth-provider';
import { BotProvider } from './providers/bot-provider';
import { AppShell } from './components/layout/app-shell';
import { AuthGuard } from './components/auth-guard';

function lazyPage<T extends Record<string, ComponentType<any>>>(
  importer: () => Promise<T>,
  name: keyof T,
) {
  return lazy(async () => {
    const mod = await importer();
    const Component = mod[name];
    return { default: Component };
  });
}

const LoginPage = lazyPage(() => import('./pages/login'), 'LoginPage');
const Showcase = lazyPage(() => import('./pages/showcase'), 'Showcase');
const DashboardPage = lazyPage(() => import('./pages/dashboard'), 'DashboardPage');
const ProductsPage = lazyPage(() => import('./pages/products'), 'ProductsPage');
const OrdersPage = lazyPage(() => import('./pages/orders'), 'OrdersPage');
const PaymentsPage = lazyPage(() => import('./pages/payments'), 'PaymentsPage');
const VaultPage = lazyPage(() => import('./pages/vault'), 'VaultPage');
const WalletPage = lazyPage(() => import('./pages/wallet'), 'WalletPage');
const WorkflowsPage = lazyPage(() => import('./pages/workflows'), 'WorkflowsPage');
const BotPage = lazyPage(() => import('./pages/bot'), 'BotPage');
const BroadcastsPage = lazyPage(() => import('./pages/broadcasts'), 'BroadcastsPage');
const CustomersPage = lazyPage(() => import('./pages/customers'), 'CustomersPage');
const TicketsPage = lazyPage(() => import('./pages/tickets'), 'TicketsPage');
const AiPage = lazyPage(() => import('./pages/ai'), 'AiPage');
const AnalyticsPage = lazyPage(() => import('./pages/analytics'), 'AnalyticsPage');
const ReportsPage = lazyPage(() => import('./pages/reports'), 'ReportsPage');
const StoragePage = lazyPage(() => import('./pages/storage'), 'StoragePage');
const BackupPage = lazyPage(() => import('./pages/backup'), 'BackupPage');
const AccessHistoryPage = lazyPage(() => import('./pages/access-history'), 'AccessHistoryPage');
const NotificationsPage = lazyPage(() => import('./pages/notifications'), 'NotificationsPage');
const MonitoringPage = lazyPage(() => import('./pages/monitoring'), 'MonitoringPage');
const TeamPage = lazyPage(() => import('./pages/team'), 'TeamPage');
const SettingsPage = lazyPage(() => import('./pages/settings'), 'SettingsPage');

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

if (import.meta.env.VITE_SENTRY_DSN != null && import.meta.env.VITE_SENTRY_DSN !== '') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

function Fallback({ error }: { error: Error }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="glass max-w-md rounded-xl p-6 text-center">
        <h1 className="mb-2 text-xl font-semibold text-danger">Something went wrong</h1>
        <p className="text-muted text-sm">{error.message}</p>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<Fallback error={new Error('Sentry boundary error')} />}>
      <ErrorBoundary FallbackComponent={Fallback}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <ThemeProvider>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/setup" element={<Navigate to="/login" replace />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/showcase" element={<Showcase />} />
                    <Route element={<AuthGuard />}>
                      <Route element={<BotProvider><AppShell /></BotProvider>}>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/products" element={<ProductsPage />} />
                        <Route path="/orders" element={<OrdersPage />} />
                        <Route path="/payments" element={<PaymentsPage />} />
                        <Route path="/vault" element={<VaultPage />} />
                        <Route path="/wallet" element={<WalletPage />} />
                        <Route path="/workflows" element={<WorkflowsPage />} />
                        <Route path="/bot" element={<BotPage />} />
                        <Route path="/broadcasts" element={<BroadcastsPage />} />
                        <Route path="/customers" element={<CustomersPage />} />
                        <Route path="/tickets" element={<TicketsPage />} />
                        <Route path="/ai" element={<AiPage />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/reports" element={<ReportsPage />} />
                        <Route path="/storage" element={<StoragePage />} />
                        <Route path="/backup" element={<BackupPage />} />
                        <Route path="/access-history" element={<AccessHistoryPage />} />
                        <Route path="/notifications" element={<NotificationsPage />} />
                        <Route path="/monitoring" element={<MonitoringPage />} />
                        <Route path="/team" element={<TeamPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                      </Route>
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
                <Toaster position="top-right" richColors />
              </ThemeProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
